import Select, { StylesConfig, components, OptionProps, InputProps, MenuProps, NoticeProps } from 'react-select';
import OptionTypeBase from 'react-select';

import { FiCheck } from 'react-icons/fi';
import { useRef } from 'react';

// O react-select 5.7 não dá papel ao menu nem às opções, e o input aponta para o menu como popup
// (`aria-controls` = id do `Menu`): sem `listbox`/`option`, o popup do combobox fica inválido (IBM
// combobox_haspopup_valid).
const Menu: React.FC<MenuProps<OptionTypeBase, false>> = props => (
  <components.Menu {...props} innerProps={{ ...props.innerProps, role: 'listbox', 'aria-label': props.selectProps['aria-label'] } as any} />
);

// "Sem opções" e "Carregando" aparecem dentro do `listbox`, que só pode ter `option` como filho
// (axe aria-required-children): viram opções desabilitadas.
const AVISO_DO_MENU = { role: 'option', 'aria-disabled': true, 'aria-selected': false };
const NoOptionsMessage: React.FC<NoticeProps<OptionTypeBase, false>> = props => (
  <components.NoOptionsMessage {...props} innerProps={{ ...props.innerProps, ...AVISO_DO_MENU } as any} />
);
const LoadingMessage: React.FC<NoticeProps<OptionTypeBase, false>> = props => (
  <components.LoadingMessage {...props} innerProps={{ ...props.innerProps, ...AVISO_DO_MENU } as any} />
);

// O nome da opção vem do texto; o ícone de selecionado é decorativo.
const Option: React.FC<OptionProps<OptionTypeBase>> = props => (
  <components.Option {...props} innerProps={{ ...props.innerProps, role: 'option', 'aria-selected': props.isSelected } as any}>
    <div className={`flex items-center justify-between ${props.isFocused ? 'focus-indicator' : ''}`}>
      {props.label}
      {props.isSelected && <FiCheck aria-hidden="true" className="text-green-500 text-lg" />}
    </div>
  </components.Option>
);

// O react-select 5.7 monta o `aria-describedby` do input sozinho (placeholder
// ou live region) e não aceita prop para acrescentar ids. A prop customizada
// `ariaDescribedBy` (declarada em `types/react-select.d.ts`) chega aqui por
// `selectProps` e é somada à dele.
const Input: React.FC<InputProps<OptionTypeBase, false>> = props => {
  const describedBy = [props['aria-describedby'], props.selectProps.ariaDescribedBy].filter(Boolean).join(' ');

  // O popup é o `listbox` do `Menu`: `aria-haspopup` diz qual (o react-select 5.7 põe `true`).
  return <components.Input {...props} aria-describedby={describedBy || undefined} aria-haspopup="listbox" />;
};

const selectComponents = { Menu, Option, Input, NoOptionsMessage, LoadingMessage };

/** Opção do `CustomSelect`: o rótulo mostrado e o valor (o `react-select` repassa a opção inteira no `onChange`). */
export interface OpcaoDoCustomSelect {
  label: string;
  value: string | number;
}

export interface CustomSelectProps {
  options:
    | OptionTypeBase[]
    | {
        label: string;
        value: string | number;
      }[];
  placeholder?: string;
  value?: any;
  onChange?: (option: OpcaoDoCustomSelect | null) => void;
  name?: string;
  className?: string;
  onBlur?: () => void;
  disabled?: boolean;
  onInputChange?: (e: any) => void;
  /** `id` do input interno, para ligar um `<label htmlFor>` de verdade ao campo. */
  inputId?: string;
  /** Substitui o rótulo genérico quando a tela tem um nome melhor para o filtro. */
  ariaLabel?: string;
  /**
   * Tema do campo. `dark` é o das telas ainda não migradas e segue sendo o
   * padrão; `sidebar` e `light` são os dois campos do rebranding — o do painel
   * azul (texto azul em negrito) e o do corpo da página (com borda cinza).
   */
  variant?: SelectVariant;
  /** Marca o campo como inválido (`aria-invalid` no input interno). */
  ariaInvalid?: boolean;
  /** `id` da mensagem de erro (`aria-errormessage` no input interno). */
  ariaErrorMessage?: string;
  /** Ids de dica/erro somados ao `aria-describedby` do input interno. */
  ariaDescribedBy?: string;
  /**
   * Regra própria para marcar a opção escolhida. Sem ela vale a do `react-select`, que compara
   * pelo `value` da opção.
   */
  isOptionSelected?: (option: any, selectValue: readonly any[]) => boolean;
}

export type SelectVariant = 'dark' | 'sidebar' | 'light';

const customStyles: StylesConfig<OptionTypeBase, false> = {
  control: provided => ({
    ...provided,
    backgroundColor: '#021C2F',
    borderRadius: '6px',
    borderColor: 'transparent',
    color: 'rgba(255, 255, 255, 0.50)',
  }),
  singleValue: provided => ({
    ...provided,
    color: 'rgba(255, 255, 255, 0.50)',
  }),
  indicatorSeparator: () => ({
    display: 'none',
  }),
  dropdownIndicator: (provided, state) => ({
    ...provided,
    color: '#7459C8',
    transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
    transition: 'transform 0.3s ease-in-out',
  }),
  menu: provided => ({
    ...provided,
    backgroundColor: '#021C2F',
  }),
  option: (provided, state) => ({
    ...provided,
    borderBottom: '2px solid #022138',
    margin: '0 auto',
    color: 'rgba(255, 255, 255, 0.50)',
    backgroundColor: state.isFocused ? '#022138' : '#021C2F',
    '&:hover': {
      backgroundColor: '#022138',
    },
    '&:focus': {
      backgroundColor: '#021C2F',
    },
    '&:active': {
      backgroundColor: '#021C2F',
    },
  }),
  input: provided => ({
    ...provided,
    color: 'white',
  }),
};

// Campos do rebranding. Reproduzem os dois `<select>` do Claude Design: o do
// painel lateral (Home.dc.html) e o do corpo da página (Regulamento.dc.html).
// A `sidebar` (seletor de ciclo) está presa em `__tests__/styles.spec.ts` com
// a saída de antes da T5; só a `light` segue os tokens de campo (CAMPO-07).
function buildLightStyles(variant: Exclude<SelectVariant, 'dark'>): StylesConfig<OptionTypeBase, false> {
  const isSidebar = variant === 'sidebar';
  // A sidebar mantém o fallback legado para não mudar de aparência (BASE-06).
  const focusedOptionBackground = isSidebar ? 'var(--background-selected, #E0EAFF)' : 'var(--shades-surface-100)';

  const sidebarControl: StylesConfig<OptionTypeBase, false>['control'] = provided => ({
    ...provided,
    minHeight: 'auto',
    padding: '4px 4px 4px 8px',
    backgroundColor: 'var(--brand-white)',
    borderRadius: '6px',
    border: 'none',
    boxShadow: 'none',
    cursor: 'pointer',
    '&:hover': { border: 'none' },
  });

  // Foco e erro trocam a borda cinza de 1px pela rosa de 2px; o padding perde
  // 1px de cada lado para o texto não pular.
  // Com erro, a borda rosa já aparece sem foco; em foco, o outline azul-escuro é
  // a mudança visual (A11Y-001). Sem erro, o outline é transparente: some na
  // tela, mas aparece em cores forçadas.
  const fieldControl: StylesConfig<OptionTypeBase, false>['control'] = (provided, state) => {
    const isInvalid = state.selectProps['aria-invalid'] === true;
    const isHighlighted = state.isFocused || isInvalid;
    const border = isHighlighted ? '2px solid var(--brand-pink)' : '1px solid var(--shades-surface-200)';
    const focusOutline = state.isFocused
      ? { outline: `2px solid ${isInvalid ? 'var(--brand-blue-dark)' : 'transparent'}`, outlineOffset: '2px' }
      : {};

    return {
      ...provided,
      ...focusOutline,
      minHeight: 'auto',
      padding: isHighlighted ? '2px 3px 2px 7px' : '3px 4px 3px 8px',
      backgroundColor: state.isDisabled ? 'var(--shades-surface-100)' : 'var(--brand-white)',
      borderRadius: 'var(--radius-field)',
      border,
      boxShadow: 'none',
      cursor: 'pointer',
      '&:hover': { border },
    };
  };

  return {
    control: isSidebar ? sidebarControl : fieldControl,
    singleValue: (provided, state) => ({
      ...provided,
      color: isSidebar ? 'var(--brand-blue)' : state.isDisabled ? 'var(--text-soft)' : 'var(--text-default)',
      fontFamily: isSidebar ? 'var(--font-display)' : 'var(--font-text)',
      fontWeight: isSidebar ? 700 : 400,
      fontSize: isSidebar ? '16px' : '13px',
    }),
    placeholder: provided => ({
      ...provided,
      color: 'var(--text-soft)',
      fontSize: isSidebar ? '16px' : '13px',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: 'var(--brand-blue)',
      transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)',
      transition: 'transform 0.3s ease-in-out',
      ...(!isSidebar && { '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }),
    }),
    menu: provided => ({
      ...provided,
      backgroundColor: 'var(--brand-white)',
      borderRadius: isSidebar ? '8px' : 'var(--radius-md)',
      overflow: 'hidden',
      zIndex: 30,
    }),
    option: (provided, state) => ({
      ...provided,
      // A escolhida em azul-escuro: o azul sobre o fundo da opção em foco dá 4,34:1 na `light` (PAD-40) e 4,03:1 na
      // `sidebar`, que passou a aparecer em todas as telas (CIC-40).
      color: state.isSelected ? 'var(--brand-blue-dark)' : 'var(--text-default)',
      fontFamily: 'var(--font-text)',
      fontSize: '13px',
      backgroundColor: state.isFocused ? focusedOptionBackground : 'var(--brand-white)',
      // Na `light`, o fundo da opção em foco sozinho tem pouco contraste: o filete azul marca o foco (A11Y-002).
      ...(!isSidebar && state.isFocused && { boxShadow: 'inset 3px 0 0 var(--brand-blue)' }),
      cursor: 'pointer',
      '&:active': { backgroundColor: focusedOptionBackground },
    }),
    input: provided => ({
      ...provided,
      color: 'var(--text-default)',
    }),
  };
}

const lightStyles = buildLightStyles('light');
const sidebarStyles = buildLightStyles('sidebar');

/** Estilos do `react-select` por variante. Exportado para os testes que prendem o visual. */
export function stylesFor(variant: SelectVariant) {
  if (variant === 'sidebar') return sidebarStyles;
  if (variant === 'light') return lightStyles;

  return customStyles;
}

function CustomSelect({
  options,
  placeholder,
  value,
  onChange,
  name,
  className,
  onBlur,
  onInputChange,
  disabled,
  inputId,
  ariaLabel,
  variant = 'dark',
  ariaInvalid,
  ariaErrorMessage,
  ariaDescribedBy,
  isOptionSelected,
}: CustomSelectProps) {
  const selectRef = useRef<any>(null);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      if (selectRef.current) {
        selectRef.current.blur();
        selectRef.current.focus();
      }
    }
  };

  const handleChange = (selectedOption: OpcaoDoCustomSelect | null) => {
    if (onChange) {
      onChange(selectedOption);
    }
    if (selectRef.current) {
      selectRef.current.blur();
      selectRef.current.focus();
    }
  };

  return (
    <Select
      isDisabled={disabled}
      inputId={inputId}
      ref={selectRef}
      onInputChange={onInputChange}
      onBlur={onBlur}
      value={value}
      onChange={onChange}
      options={options}
      isOptionSelected={isOptionSelected}
      styles={stylesFor(variant)}
      placeholder={placeholder}
      name={name}
      components={selectComponents}
      className={className}
      isSearchable
      // Na `light`, o blur/focus a cada Enter/Espaço apagava a busca e disparava `onBlur` (A11Y-003).
      onKeyDown={variant === 'light' ? undefined : handleKeyDown}
      aria-live="assertive"
      aria-label={ariaLabel ?? 'Selecione uma opção'}
      aria-invalid={ariaInvalid}
      aria-errormessage={ariaErrorMessage}
      ariaDescribedBy={ariaDescribedBy}
    />
  );
}

export default CustomSelect;
